import React, { FC, useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import UAParser from "ua-parser-js";

import { getDataLayerContext } from "lib/analytics/datalayerHelpers";
import { useCookies } from "lib/cookie-monster";
import { useMediaContext } from "lib/media-query";

const SlideUp = keyframes`
 0% { bottom: -250px; opacity: 0 }
 60% { bottom: 0px; opacity: 1 }
 100% { bottom: -30px; opacity: 1 }
`;

const AppPromptContainer = styled.div`
    display: flex;
    position: fixed;
    top: 0px;
    left: 0px;
    right: 0px;
    bottom: 0px;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 9999;
    animation: fadeIn 0.3s;
    touch-action: none;
`;

const AppPromptSheet = styled.div`
    display: flex;
    flex-direction: column;
    position: absolute;
    width: 100%;
    height: 280px;
    bottom: -30px;
    background-color: ${({ theme }) => theme.colors.white};
    box-shadow: 0px -2px 3.33333px rgba(0, 0, 0, 0.25);
    border-radius: 16px 16px 0px 0px;
    animation-name: ${SlideUp};
    animation-duration: 1s;
    animation-iteration-count: 1;
    padding: 0px 36px 40px 36px;
`;

const AppPromptTitle = styled.div`
    width: 100%;
    padding: 16px;
    font-weight: 600;
    font-size: 16px;
    line-height: 15px;
    text-align: center;
    border-bottom: ${({ theme }) => theme.colors.breathing};
`;

const AppPromptOption = styled.div`
    display: flex;
    align-items: center;
    margin-top: 16px;
`;

const AppPromptIcon = styled.img`
    border-radius: 14px;
    width: 60px;
    height: 60px;
    margin-right: 16px;
    justify-self: start;
    box-shadow: 0 1px 1px rgba(0, 0, 0, 0.12), 0 2px 2px rgba(0, 0, 0, 0.12),
        0 3px 3px 3px rgba(0, 0, 0, 0.05);
`;
const AppPromptLabel = styled.div`
    font-weight: 400;
    justify-self: start;
`;

const AppPromptCTA = styled.a`
    background-color: ${({ theme }) => theme.colors.tertiary[600]};
    color: ${({ theme }) => theme.colors.tertiary[800]};
    width: 100px;
    height: 48px;
    padding: 5px 10px;
    line-height: 38px;
    margin-left: auto;
    border-radius: 24px;
    text-align: center;
    &.primary {
        background: ${({ theme }) => theme.colors.secondary[700]};
        color: ${({ theme }) => theme.colors.white};
    }
`;

type BrowserProps = { browserIcon: string; browserName: string };

export const getAppPromptInfo = (userAgent: string): BrowserProps => {
    const browserRawName = userAgent ? new UAParser(userAgent).getBrowser().name : "Webbrowser"; // possible names Chrome WebView, Mobile Safari, Chrome, Safari...

    if (["Chrome", "Firefox", "Safari"].some((v) => browserRawName?.includes(v))) {
        let browserName = "";
        if (browserRawName?.includes("Chrome")) {
            browserName = "Chrome";
        }
        if (browserRawName?.includes("Safari")) {
            browserName = "Safari";
        }

        if (browserRawName?.includes("Firefox")) {
            browserName = "Firefox";
        }

        return {
            browserIcon: `/static/images/browser-icons/${browserName}.png`,
            browserName
        };
    }

    return {
        browserIcon: "/static/images/browser-icons/Webbrowser.png",
        browserName: "Webbrowser"
    };
};

export const triggerAppPromptAnalyticsEvent = (eventAction: string): void => {
    const dataLayerContext = getDataLayerContext();

    dataLayerContext?.push({
        event: "GAevent",
        eventCategory: "appPrompt",
        eventAction
    });
};

export const MobileAppPrompt: FC = () => {
    const { cookies, setCookie } = useCookies();
    const { isMobile, isMobilePlus } = useMediaContext();
    const [showPrompt, setShowPrompt] = useState<boolean>(false);
    const [appPromptInfo, setAppPromptInfo] = useState<BrowserProps>(getAppPromptInfo("none"));

    const dismissPrompt = () => {
        setCookie("suppressAppPrompt", "true", { maxAge: 604800 }); // seven days
        setShowPrompt(false);
        triggerAppPromptAnalyticsEvent("continueOnSite");

        // Trigger Usabilla survey
        if (process.browser && window.usabilla_live) {
            window.usabilla_live("virtualPageView");
            window.usabilla_live("trigger", "mobileAppPrompt_useWeb");
        }
    };

    const useApp = () => {
        setCookie("suppressAppPrompt", "true", { maxAge: 86400 }); // one day
        setShowPrompt(false);
        triggerAppPromptAnalyticsEvent("useApp");
    };

    useEffect(() => {
        setAppPromptInfo(getAppPromptInfo(window.navigator.userAgent || navigator.vendor));
        const includedPages = ["/aanbod", "/a"].some((str) =>
            window.location.pathname.includes(str)
        );

        if (
            cookies.suppressAppPrompt !== "true" &&
            (isMobile || isMobilePlus) &&
            /android|iPad|iPhone|iPod/i.test(navigator.userAgent || navigator.vendor) &&
            includedPages
        ) {
            setShowPrompt(true);
            triggerAppPromptAnalyticsEvent("showPrompt");
        }
    }, []);

    if (!showPrompt) {
        return null;
    }

    return (
        <AppPromptContainer>
            <AppPromptSheet>
                <AppPromptTitle>Vind je volgende auto met</AppPromptTitle>
                <AppPromptOption>
                    <AppPromptIcon
                        src="/static/images/autotrack-app-icon-new.png"
                        alt="AutoTrack App Icon"
                    />
                    <AppPromptLabel>AutoTrack App</AppPromptLabel>
                    <AppPromptCTA
                        className="primary"
                        data-testid="app-prompt-cta"
                        target="_BLANK"
                        href="/app?ct=WebsiteAppPrompt&utm_source=AutoTrack&utm_campaign=WebsiteAppPrompt"
                        onClick={useApp}
                    >
                        Open
                    </AppPromptCTA>
                </AppPromptOption>
                <AppPromptOption>
                    <AppPromptIcon src={appPromptInfo.browserIcon} alt="Browser Icon" />
                    <AppPromptLabel>{appPromptInfo.browserName}</AppPromptLabel>
                    <AppPromptCTA
                        data-testid="app-dismiss-prompt-cta"
                        onClick={() => dismissPrompt()}
                    >
                        Doorgaan
                    </AppPromptCTA>
                </AppPromptOption>
            </AppPromptSheet>
        </AppPromptContainer>
    );
};
